import React, { useEffect, useState } from "react";
import { getAllHeaders } from "@api";
import { useStore } from "@util/states";
import { HeaderDesktopWrapper, MegaNavWrapper } from "./headerDesktop.style";
import { Image, Link, SearchField } from "@global";
import { SanityMegaNav, SanityLinkOrMegaNav, Maybe } from "@graphql-types";
import { HeaderDesign, isSanityMegaNav } from "@util/types";
import { IconBurger, IconDownChevron } from "@util/assets";
import { HeadingStyle } from "@util/standard";
import { useCheckScreenSize } from "@util/hooks";
import HeaderNavMobile from "./headerNavMobile";
import { isBrowser, linkHomeAttributes } from "@util/helper";
interface Props {
  design: HeaderDesign;
}

export default function HeaderDesktop({ design }: Props) {
  const { largeUp, largeDown } = useCheckScreenSize();
  const { currentRegion } = useStore();
  const [headerFixed, setHeaderFixed] = useState(false);
  const [headerHide, setHeaderHide] = useState(false);
  const [headerMinimise, setHeaderMinimise] = useState(false);
  const [headerSolid, setHeaderSolid] = useState(false);
  const [openMobileNav, setOpenMobileNav] = useState(false);
  const [megaNavActive, setMegaNavActive] = useState(-1);
  const [wrapperScrollTop, setWrapperScrollTop] = useState(0);
  const [pathName, setPathName] = useState("");

  const selectedHeader = getAllHeaders(
    currentRegion.iban ? currentRegion.iban : (process.env.GATSBY_DEFAULT_IBAN_REGION as string),
  );

  if (selectedHeader == null) return null;

  const { logo, links } = selectedHeader;

  useEffect(() => {
    if (location.pathname) {
      setPathName(location.pathname);
    }
  }, [isBrowser() && location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setWrapperScrollTop((prev) => {
        const scrollTopWrapper = document.querySelector('.full-page-scroll')?.scrollTop || window.pageYOffset;
        if (scrollTopWrapper > prev) {
          setHeaderFixed(true);
          setHeaderMinimise(true);
          setHeaderHide(true);
        }
        else if (scrollTopWrapper === 0) {
          setHeaderFixed(false);
          setHeaderMinimise(false);
        }
        else {
          setHeaderFixed(true);
          setHeaderMinimise(true);
          setHeaderHide(false);
        }
        return scrollTopWrapper;
      });
    }
    document.querySelector('.full-page-scroll')?.addEventListener('scroll', handleScroll);
    document.addEventListener('scroll', handleScroll);
    return () => {
      document.querySelector('.full-page-scroll')?.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const organiseDesign = () => {
    if (!headerFixed && design && headerSolid) {
      return "solid";
    }
    if (headerFixed) {
      return "solid";
    }
    return design;
  };

  const { linkHomeAttr } = linkHomeAttributes(selectedHeader.region);
  const headerPath = pathName.includes(`brands/x-shot/insanity`);
  const productRecallPath = pathName.includes("voluntary-recall-notice") || pathName.includes("our-response");
  return (
    <HeaderDesktopWrapper className="header-wrp" design={organiseDesign()} fixed={headerFixed} minimise={headerMinimise} hide={headerHide}>

      <div className={headerPath ? 'instanity-header' : "col-left"}>
        {logo &&
          <div className={headerPath ? "instanity-header-logo" : "header-logo"}>
            <Link {...linkHomeAttr}>
              <Image data={logo} />
            </Link>
          </div>
        }
        {links && largeUp && !productRecallPath && (
          <nav>
            <ul className="level-one-ul">
              {links.map((levelOne, index) => (
                <LevelOneItem
                  key={levelOne?._key}
                  data={levelOne}
                  headerSolidState={{ headerSolid, setHeaderSolid }}
                  thisIndex={index}
                  megaNavActiveState={{ megaNavActive, setMegaNavActive }}
                />
              ))}
            </ul>
          </nav>
        )}
      </div>

      {!productRecallPath &&
        <div className="col-right">
          {largeUp && <SearchField design={organiseDesign()} />}
          {largeDown && <IconBurger onClick={() => setOpenMobileNav(!openMobileNav)} />}
        </div>}
      {largeDown && !productRecallPath && (
        <HeaderNavMobile
          data={selectedHeader}
          openMobileState={{ openMobileNav, setOpenMobileNav }}
        />
      )}

    </HeaderDesktopWrapper>
  );
}

interface LevelOneItemProps {
  data: Maybe<SanityLinkOrMegaNav>;
  headerSolidState: any;
  megaNavActiveState: any;
  thisIndex: number;
}

function LevelOneItem({
  data,
  headerSolidState,
  megaNavActiveState,
  thisIndex,
}: LevelOneItemProps) {
  const [megaNavOpen, setMegaNavOpen] = useState(false);
  const { setHeaderSolid } = headerSolidState;
  const { megaNavActive, setMegaNavActive } = megaNavActiveState;

  if (data == null) return null;
  const { linkText } = data;

  const handleClick = () => {
    if (megaNavOpen === false) {
      setMegaNavOpen(true);
      setHeaderSolid(true);
      setMegaNavActive(thisIndex);
      return;
    }
    setMegaNavOpen(false);
    setHeaderSolid(false);
    setMegaNavActive(-1);
    return;
  };

  return (
    <li className="level-one-li">
      {!isSanityMegaNav(data) && <Link {...data} className="level-one-a" />}
      {isSanityMegaNav(data) && (
        <>
          <a
            tabIndex={0}
            role="button"
            className={`level-one-a a-dropdown ${megaNavOpen && megaNavActive === thisIndex && `active`}`}
            onClick={() => handleClick()}
          >
            <span>{linkText}</span>
            <IconDownChevron />
          </a>
          {megaNavOpen && megaNavActive === thisIndex && (
            <MegaNav data={data} mouseLeave={() => handleClick()} />
          )}
        </>
      )}
    </li>
  );
}

interface MegaNavProps {
  data: SanityMegaNav;
  mouseLeave: () => void;
}

function MegaNav({ data, mouseLeave }: MegaNavProps) {
  const { design, dropDown, megaNavCta, dropDownPlain } = data;
  if (design == null) return null;
  const dropDownContent =
    (design === `designPlain` && dropDownPlain) || (design === `designBrand` && dropDown);

  return (
    <MegaNavWrapper design={design} onMouseLeave={mouseLeave}>
      <div className="mega-nav-inner">
        <ul>
          {dropDownContent &&
            dropDownContent.map(link => (
              <li>
                <Link {...link} />
              </li>
            ))}
        </ul>
        {megaNavCta && (
          <article>
            <div className="cta-inner">
              {megaNavCta.title && <HeadingStyle inverse={true}>{megaNavCta.title}</HeadingStyle>}
              <Link
                useIcon="iconRightArrow"
                design="outlineLight"
                internalLink={megaNavCta.internalLink}
                externalLink={megaNavCta.externalLink}
                linkText={megaNavCta.linkText}
              />
            </div>

            {megaNavCta.backgroundImage && (
              <Image data={megaNavCta.backgroundImage} isBackground={true} />
            )}
          </article>
        )}
      </div>
    </MegaNavWrapper>
  );
}
